<script lang="ts">
	/**
	 * This customer's test clock, on their support page — the quick way to watch a renewal, a
	 * dunning run or a period-end downgrade happen to one person.
	 *
	 * One block per **test-mode** provider that has clocks. The server answers an empty list in a
	 * live store, and the panel then renders nothing at all, so production customer pages are
	 * untouched.
	 *
	 * Moving a customer onto a clock is **permanent** at the provider, and deleting the clock later
	 * deletes the customer there too — which is why it is a confirmed action naming both. Once the
	 * clock settles after an advance, the customer is re-read from the provider straight away and the
	 * page is told to reload (`changed`): advancing runs renewals in seconds, and the webhooks
	 * announcing them may never reach a test store, so without this the page would show the old
	 * period until the nightly pass.
	 *
	 * Its own read, separately guarded, like `VisitsPanel`: a provider that cannot be asked must not
	 * cost support the rest of the customer page.
	 */

	import { createEventDispatcher, onDestroy } from 'svelte';
	import { Button } from 'flowbite-svelte';
	import { ConfirmButton, EmptyState, Panel, TestClockControls } from '../../../components';
	import {
		advanceTestClock,
		createTestClock,
		listCustomerTestClocks,
		resyncCustomerFromProvider
	} from '../../../AppNetwork';
	import { anyClockAdvancing, CLOCK_POLL_MS, formatClockTime } from '../../../components/testClocks';
	import { toastError, toastSuccess, toastWarning } from '../../../stores/toast';
	import type { CustomerTestClock, ResyncResult } from '../../../AppTypes';

	/** The **user's** uuid, which is how a customer is addressed everywhere on this screen. */
	export let uuid: string = '';
	/** The customer's display name, for the confirmation. */
	export let name: string = '';

	const dispatch = createEventDispatcher<{ changed: ResyncResult }>();

	let entries: CustomerTestClock[] = [];
	let loading: boolean = true;
	let loadError: string = '';
	/** The provider a request is in flight for. */
	let busyUUID: string = '';
	/** Providers whose clock was advancing on the last read, so settling can be noticed. */
	let advancing = new Set<string>();
	let pollTimer: ReturnType<typeof setTimeout> | undefined;

	$: void fnLoad(uuid);

	async function fnLoad(userUUID: string, quiet = false): Promise<void> {
		clearTimeout(pollTimer);
		if (!quiet) {
			loading = true;
		}
		loadError = '';
		try {
			entries = await listCustomerTestClocks(userUUID);
			await fnSettle();
		} catch (error) {
			loadError = (error as Error).message;
		} finally {
			loading = false;
		}
	}

	/**
	 * Re-reads the customer from every provider whose clock has just finished advancing, then
	 * schedules another look while any clock is still moving.
	 */
	async function fnSettle(): Promise<void> {
		const settled = entries.filter(entry => advancing.has(entry.provider.uuid) && entry.clock?.status !== 'advancing');
		advancing = new Set(entries.filter(entry => entry.clock?.status === 'advancing').map(entry => entry.provider.uuid));
		for (const entry of settled) {
			await fnResync(entry, true);
		}
		if (anyClockAdvancing(entries.map(entry => entry.clock))) {
			pollTimer = setTimeout(() => void fnLoad(uuid, true), CLOCK_POLL_MS);
		}
	}

	function fnReplace(updated: CustomerTestClock): void {
		entries = entries.map(entry => (entry.provider.uuid === updated.provider.uuid ? updated : entry));
		if (updated.clock?.status === 'advancing') {
			advancing = new Set([...advancing, updated.provider.uuid]);
		}
		void fnSettle();
	}

	async function fnAttach(entry: CustomerTestClock): Promise<void> {
		busyUUID = entry.provider.uuid;
		try {
			const clock = await createTestClock(entry.provider.uuid, { name: `${name || 'Customer'} test`, userUUID: uuid });
			fnReplace({ ...entry, clock, providerCustomerID: entry.providerCustomerID ?? clock.customers[0]?.providerCustomerID ?? null });
			toastSuccess('Moved onto a test clock.', `It stands at ${formatClockTime(clock.frozenTime)}.`);
		} catch (error) {
			toastError('The customer was not moved onto a test clock.', (error as Error).message);
		} finally {
			busyUUID = '';
		}
	}

	async function fnAdvance(entry: CustomerTestClock, frozenTime: string): Promise<void> {
		if (entry.clock === null) {
			return;
		}
		busyUUID = entry.provider.uuid;
		try {
			const clock = await advanceTestClock(entry.provider.uuid, entry.clock.id, frozenTime);
			fnReplace({ ...entry, clock });
		} catch (error) {
			toastError('The clock was not advanced.', (error as Error).message);
		} finally {
			busyUUID = '';
		}
	}

	/** `automatic` after an advance settles: quiet unless something needs saying. */
	async function fnResync(entry: CustomerTestClock, automatic = false): Promise<void> {
		busyUUID = entry.provider.uuid;
		try {
			const result = await resyncCustomerFromProvider(uuid, entry.provider.uuid);
			if (result.anomalies.some(line => line.includes('could not be re-fetched'))) {
				toastWarning('Some subscriptions could not be re-read.', result.anomalies.join(' '));
			} else if (!automatic || result.corrected > 0) {
				toastSuccess(
					`Re-read ${result.checked} subscription${result.checked === 1 ? '' : 's'} from ${entry.provider.name}.`,
					result.corrected === 0 ? 'Nothing had changed.' : `${result.corrected} had moved on and were corrected.`
				);
			}
			dispatch('changed', result);
		} catch (error) {
			toastError('The customer was not re-read.', (error as Error).message);
		} finally {
			busyUUID = '';
		}
	}

	onDestroy(() => clearTimeout(pollTimer));
</script>

{#if loading || loadError.length > 0 || entries.length > 0}
	<Panel title="Test clock" faIcon="clock" class="mt-6">
		{#if loading}
			<EmptyState variant="loading" title="Loading test clocks…" />
		{:else if loadError.length > 0}
			<EmptyState variant="error" title="The test clocks could not be loaded." description={loadError}>
				<Button color="alternative" size="sm" on:click={() => fnLoad(uuid)}>Try again</Button>
			</EmptyState>
		{:else}
			<div class="space-y-3">
				{#each entries as entry (entry.provider.uuid)}
					<div class="widget bg-depth space-y-3" data-testid="customer-test-clock">
						<div class="flex flex-wrap items-center gap-2">
							<span class="font-semibold">{entry.provider.name}</span>
							{#if entry.providerCustomerID !== null}
								<code class="text-xs text-default">{entry.providerCustomerID}</code>
							{/if}
							<span class="grow" />
							{#if entry.clock !== null}
								<Button
									size="xs"
									color="alternative"
									disabled={busyUUID === entry.provider.uuid || entry.clock.status === 'advancing'}
									on:click={() => fnResync(entry)}
								>
									<i class="fa-solid fa-rotate mr-1" />Re-read from provider
								</Button>
							{/if}
						</div>

						{#if entry.error !== null}
							<EmptyState variant="error" title="This provider could not be asked." description={entry.error} />
						{:else if entry.clock !== null}
							<TestClockControls
								clock={entry.clock}
								busy={busyUUID === entry.provider.uuid}
								on:advance={event => fnAdvance(entry, event.detail.frozenTime)}
							/>
							<p class="text-xs text-default">
								Clock <code>{entry.clock.id}</code>. Once an advance settles, this customer is re-read from the provider
								and the page reloads.
							</p>
						{:else}
							<p class="text-sm text-default">
								Not on a test clock. Moving them onto one lets you advance time for this customer alone and watch
								renewals, failed payments and cancellations happen in seconds.
							</p>
							<ConfirmButton
								size="sm"
								color="primary"
								disabled={busyUUID === entry.provider.uuid}
								confirmText={`Move ${name || 'this customer'} onto a new test clock at ${entry.provider.name}? This cannot be undone: they stay on the clock, and deleting the clock deletes them at the provider with their subscriptions.`}
								on:confirm={() => fnAttach(entry)}
							>
								{#if busyUUID === entry.provider.uuid}
									<i class="fa-solid fa-spinner fa-spin mr-2" />
								{:else}
									<i class="fa-solid fa-clock mr-2" />
								{/if}
								Move onto a test clock
							</ConfirmButton>
						{/if}
					</div>
				{/each}
			</div>
		{/if}
	</Panel>
{/if}
